// File: C:\Users\s-naidu\Desktop\merchant-card\frontend\src\merchant\pages\MerchantForms.tsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MerchantLayout from "../components/MerchantLayout";

type FormKey =
  | "company_overview"
  | "representative"
  | "payment_handling"
  | "products"
  | "sales_method"
  | "store_list"
  | "contact_person";

type FormItem = {
  key: FormKey;
  label: string;
  path: string;
};

const API_BASE = "/api";

export default function MerchantForms() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Per-form completion (入力済み判定) comes from backend is_completed
  const [completed, setCompleted] = useState<Record<FormKey, boolean>>({
    company_overview: false,
    representative: false,
    payment_handling: false,
    products: false,
    sales_method: false,
    store_list: false,
    contact_person: false,
  });

  // ✅ Global submission lock (提出済み判定)
  const [isSubmitted, setIsSubmitted] = useState(false);

  const token = localStorage.getItem("merchant_access_token") || "";

  const items: FormItem[] = useMemo(
    () => [
      {
        key: "company_overview",
        label: "1. 会社概要（全業種共通）",
        path: "/merchant/forms/company-overview",
      },
      { key: "representative", label: "2. 代表者様情報", path: "/merchant/forms/representative" },
      { key: "payment_handling", label: "3. 決済取扱状況", path: "/merchant/forms/payment-handling" },
      { key: "products", label: "4. 取扱商品", path: "/merchant/forms/products" },
      { key: "sales_method", label: "5. 販売方法", path: "/merchant/forms/sales-method" },
      { key: "store_list", label: "6. 店舗一覧", path: "/merchant/forms/store-list" },
      { key: "contact_person", label: "7. ご担当者様情報", path: "/merchant/forms/contact-person" },
    ],
    []
  );

  // ✅ Map each card -> backend GET endpoint
  // IMPORTANT: These are the URLs MerchantForms will call to check is_completed
  const statusEndpoints: Record<FormKey, string> = useMemo(
    () => ({
      company_overview: "/merchant/forms/company-overview",
      representative: "/merchant/forms/representative",
      payment_handling: "/merchant/forms/payment-handling",
      products: "/merchant/forms/products",
      sales_method: "/merchant/forms/sales-method",
      store_list: "/merchant/forms/store-list",
      contact_person: "/merchant/forms/contact-person",
    }),
    []
  );

  // ✅ Global submission state endpoint (already exists per your spec)
  const ME_ENDPOINT = "/merchant/me";

  const fetchAllStatuses = async (signal?: AbortSignal) => {
    setError("");

    if (!token) {
      setError("ログイン情報がありません。再ログインしてください。");
      setLoading(false);
      return;
    }

    try {
      const keys = Object.keys(statusEndpoints) as FormKey[];

      // 1) Fetch global submission lock
      const mePromise = (async () => {
        const res = await fetch(`${API_BASE}${ME_ENDPOINT}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
          signal,
        });

        if (res.status === 401) throw new Error("AUTH_401");
        if (!res.ok) return { submitted: false };

        const json = await res.json().catch(() => ({}));
        // Support both json.data.is_submitted and json.is_submitted shapes
        const submitted = Boolean(json?.data?.is_submitted ?? json?.is_submitted);
        return { submitted };
      })();

      // 2) Fetch all 7 form statuses in parallel
      const formsPromise = Promise.allSettled(
        keys.map(async (key) => {
          const url = `${API_BASE}${statusEndpoints[key]}`;

          const res = await fetch(url, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
            signal,
          });

          if (res.status === 401) throw new Error("AUTH_401");

          // If endpoint not implemented yet (404/405), treat as not completed
          if (!res.ok) {
            return { key, done: false };
          }

          const json = await res.json().catch(() => ({}));
          // ✅ Robust: support both json.data.is_completed and json.is_completed
          const done = Boolean(json?.data?.is_completed ?? json?.is_completed);
          return { key, done };
        })
      );

      const [meResult, formResults] = await Promise.allSettled([mePromise, formsPromise]);

      // Handle auth error (either call)
      const authFailed =
        (meResult.status === "rejected" && meResult.reason?.message === "AUTH_401") ||
        (formResults.status === "rejected" && formResults.reason?.message === "AUTH_401");

      if (authFailed) {
        setError("認証が切れました。再ログインしてください。");
        setLoading(false);
        return;
      }

      // Apply is_submitted (if ME failed for non-auth, keep false)
      if (meResult.status === "fulfilled") {
        setIsSubmitted(Boolean(meResult.value.submitted));
      } else {
        setIsSubmitted(false);
      }

      // Build next completed map
      const next: Record<FormKey, boolean> = {
        company_overview: false,
        representative: false,
        payment_handling: false,
        products: false,
        sales_method: false,
        store_list: false,
        contact_person: false,
      };

      if (formResults.status === "fulfilled") {
        formResults.value.forEach((r) => {
          if (r.status === "fulfilled") {
            next[r.value.key] = r.value.done;
          }
          // rejected (non-auth) => leave false
        });
      }

      setCompleted(next);
      setLoading(false);
    } catch (e: any) {
      if (e?.name === "AbortError") return;

      if (e?.message === "AUTH_401") {
        setError("認証が切れました。再ログインしてください。");
        setLoading(false);
        return;
      }

      setError("フォーム進捗の取得に失敗しました。バックエンドが起動しているか確認してください。");
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    // initial fetch
    fetchAllStatuses(controller.signal);

    // ✅ Re-fetch on focus/visibility change so the card updates immediately
    const onFocus = () => fetchAllStatuses();
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchAllStatuses();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      controller.abort();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <MerchantLayout title="フォーム入力" active="forms">
      <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 10 }}>フォーム入力</div>
      <div style={{ color: "#475569", marginBottom: 12 }}>
        各フォームを入力し、保存してください。必須項目がすべて入力されると「入力済み」表示になります。
      </div>

      {/* ✅ Submitted/locked instruction */}
      <div
        style={{
          marginBottom: 16,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1e3a8a",
          padding: "10px 12px",
          borderRadius: 12,
          fontWeight: 900,
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        すべてのフォームは、提出前であれば何度でも編集できます。
最終提出後は修正できませんのでご注意ください。

      </div>

      {loading && <div style={{ color: "#475569" }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginBottom: 12,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          {items.map((it) => {
            const done = completed[it.key];

            // ✅ Step③ Status rules
            // 未完了 (grey): required not filled
            // 入力済み (red): required filled (is_completed=true)
            // 提出済み (green): global lock (is_submitted=true)
            const statusText = isSubmitted ? "提出済み" : done ? "入力済み" : "未完了";
            const chipBg = isSubmitted ? "#16a34a" : done ? "#dc2626" : "#64748b"; // green / red / grey

            const cardBorder = isSubmitted
              ? "2px solid #16a34a"
              : done
              ? "2px solid #dc2626"
              : "1px solid #cbd5e1";

            const cardBg = isSubmitted ? "#ecfdf5" : done ? "#fff1f2" : "#f8fafc";

            const subText = isSubmitted ? "提出済み（編集不可）" : done ? "必須項目入力済み" : "未入力 / 未保存";

            return (
              <button
                key={it.key}
                onClick={() => navigate(it.path)}
                style={{
                  textAlign: "left",
                  padding: "14px 14px",
                  borderRadius: 14,
                  border: cardBorder,
                  background: cardBg,
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontWeight: 900, color: "#0f172a" }}>{it.label}</div>

                  <div
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 900,
                      background: chipBg,
                      color: "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {statusText}
                  </div>
                </div>

                <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>{subText}</div>
              </button>
            );
          })}
        </div>
      )}
    </MerchantLayout>
  );
}
